/**
 * Administration — the deeper settings: who is here, what they may do, how
 * they get in, and what this deployment still needs.
 *
 * Four things this screen is careful about, each because the opposite is the
 * comfortable mistake:
 *
 *   · **A level is not a permission.** Every control that edits one says so.
 *     Raising a level shows somebody a screen whose data the API then
 *     refuses; it does not open the data. Making that plain is the difference
 *     between an administrator who understands what they just did and one who
 *     believes they have granted something.
 *   · **Nothing here is ticked by hand.** The setup checklist is computed from
 *     the platform's own database on every read, so a step cannot be marked
 *     done while it is not, and it un-ticks itself if somebody undoes it.
 *   · **It says what it cannot do.** There is no invitation email, because the
 *     platform has no inbox; adding somebody creates the row their identity
 *     provider's subject will bind to, and the screen says that rather than
 *     implying a message was sent.
 *   · **It warns before it lists.** One administrator, a person with no role,
 *     a user with no subject: each has bitten a real deployment, and each is
 *     said at the top rather than left to be noticed in a table.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "../../app/SessionContext";
import { useScreenContext } from "../../assistant/screen";
import { LEVEL_LABEL, LEVEL_WHAT, LEVELS, type AccessLevel } from "../../assistant/access";
import { Button, Card, ErrorState, Input, Select, Skeleton } from "../../design/components";
import { Page } from "../../layout/AppShell";
import {
  administrators,
  levelOfPerson,
  orderedSteps,
  peopleWarnings,
  setupSummary,
  type Person,
  type RoleRow,
} from "../../domain/administration";
import { t, tn } from "../../i18n";
import s from "./Administration.module.css";

type Tab = "setup" | "people" | "roles" | "signin";

const LEVEL_CLASS: Record<AccessLevel, string> = {
  operations: s.lvOperations,
  engineering: s.lvEngineering,
  executive: s.lvExecutive,
  master: s.lvMaster,
};

const Level = ({ level }: { level: AccessLevel }) => (
  <span className={`${s.level} ${LEVEL_CLASS[level]}`} title={t(LEVEL_WHAT[level])}>
    {t(LEVEL_LABEL[level]).toUpperCase()}
  </span>
);

export function AdministrationPage() {
  const session = useSession();
  const source = session.source;
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("setup");

  const setup = useQuery({ queryKey: ["admin-setup"], queryFn: () => source.adminSetup(), staleTime: 15_000 });
  const people = useQuery({ queryKey: ["admin-people"], queryFn: () => source.adminPeople(), staleTime: 15_000 });
  const roles = useQuery({ queryKey: ["admin-roles"], queryFn: () => source.adminRoles(), staleTime: 60_000 });
  const auth = useQuery({ queryKey: ["admin-auth"], queryFn: () => source.adminAuthStatus(), staleTime: 60_000 });
  const refresh = () => {
    for (const k of ["admin-setup", "admin-people", "admin-roles", "admin-auth"])
      void qc.invalidateQueries({ queryKey: [k] });
  };

  const st = setup.data;
  useScreenContext(
    st
      ? {
          route: "/administration",
          title: "Administration",
          summary: [
            setupSummary(st),
            st.blocking.length ? `Blocking: ${st.blocking.join(", ")}.` : "Nothing is blocking.",
            people.data && roles.data
              ? `${people.data.filter((p) => !p.disabledAt).length} active people; ${administrators(people.data, roles.data)} can administer the platform.`
              : "The directory is still loading.",
          ],
          offers: [
            { label: "What is still needed?", ask: "What does this deployment still need before it is ready?" },
            { label: "Who can administer it?", ask: "Who holds a role that can administer this platform?" },
            { label: "What does a level do?", ask: "What does an access level decide, and what does it not?" },
          ],
        }
      : null,
  );

  const anyError = setup.error ?? people.error ?? roles.error;

  return (
    <Page
      eyebrow={t("{site} · platform", { site: session.site.code })}
      title="Administration"
      actions={
        <Button size="sm" onClick={refresh}>
          {t("Refresh")}
        </Button>
      }
    >
      <div className={s.page}>
        <p className={s.lead}>
          {rich(
            t(
              "Manage people, roles and sign-in; a person’s {level} sets the screens they see, never their permissions.",
            ),
            { level: <b>{t("access level")}</b> },
          )}
        </p>

        {anyError ? (
          <ErrorState error={anyError} retry={refresh} />
        ) : (
          <>
            <div className={s.tabs} role="tablist" aria-label={t("Administration")}>
              {(
                [
                  ["setup", "Setup", st ? `${st.done}/${st.total}` : ""],
                  ["people", "People", people.data ? String(people.data.filter((p) => !p.disabledAt).length) : ""],
                  ["roles", "Roles & access", roles.data ? String(roles.data.length) : ""],
                  ["signin", "Sign-in", ""],
                ] as [Tab, string, string][]
              ).map(([k, label, badge]) => (
                <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>
                  {t(label)}
                  {badge && <span className={s.count}>{badge}</span>}
                </button>
              ))}
            </div>

            {tab === "setup" && <SetupTab query={setup} go={setTab} />}
            {tab === "people" && <PeopleTab people={people} roles={roles} auth={auth} onChanged={refresh} />}
            {tab === "roles" && <RolesTab roles={roles} people={people} onChanged={refresh} />}
            {tab === "signin" && <SignInTab auth={auth} roles={roles} onChanged={refresh} />}
          </>
        )}
      </div>
    </Page>
  );
}

/** A translated sentence with markup: each `{name}` is replaced by its node. */
function rich(text: string, nodes: Record<string, ReactNode>): ReactNode {
  return text
    .split(/\{(\w+)\}/)
    .map((part, i) => <Fragment key={i}>{i % 2 ? (nodes[part] ?? `{${part}}`) : part}</Fragment>);
}

/* ── setup ───────────────────────────────────────────────────────────── */

/**
 * Where each unfinished step is done. A step fixed on this screen switches
 * tab; one fixed on another screen opens it; one that is the server's
 * configuration says what to change, because no button here can change how
 * the API was started — and pretending one could is how a checklist lies.
 */
const STEP_ACTION: Record<string, { tab?: Tab; route?: string; label: string } | { says: string }> = {
  people: { tab: "people", label: "Invite people" },
  roles: { tab: "people", label: "Give everybody a role" },
  admins: { tab: "people", label: "Invite a second administrator" },
  sign_in: { tab: "signin", label: "Open sign-in" },
  levels: { tab: "roles", label: "Set role levels" },
  work: { route: "/work-orders", label: "Open work orders" },
  estate: { route: "/migration", label: "Load the estate" },
  dev_header: {
    says: "Set SLT_DEV_AUTH=0 on the API service and redeploy. This can't be changed on this screen.",
  },
  packs: {
    says: "Packs are installed on the server with the pack installer, not on this screen.",
  },
  keys: {
    says: "Machine access keys are issued on the server with issue_key. Each key shows here with its scopes.",
  },
  tenant: { says: "The organization's name is set on the server when the tenant is created." },
};

function SetupTab({
  query,
  go,
}: {
  query: ReturnType<typeof useQuery<import("../../domain/administration").SetupState>>;
  go: (tab: Tab) => void;
}) {
  const navigate = useNavigate();
  if (query.isPending)
    return (
      <Card title="Setup">
        <Skeleton rows={8} />
      </Card>
    );
  const st = query.data;
  if (!st) return null;
  return (
    <Card title="What this deployment still needs" sub={setupSummary(st)}>
      <div className={s.bar} style={{ marginBottom: 12 }}>
        <span style={{ width: `${Math.round((100 * st.done) / Math.max(1, st.total))}%` }} />
      </div>
      {st.blocking.length > 0 && (
        <p className={s.stopBox} style={{ marginBottom: 10 }}>
          <b>{tn(st.blocking.length, "{n} blocking step.", "{n} blocking steps.")}</b>{" "}
          {t(
            "Blocking steps make this deployment unsafe or unusable. Don't use it for real work until they're done.",
          )}
        </p>
      )}
      <div className={s.steps}>
        {orderedSteps(st).map((x) => (
          <div key={x.key} className={`${s.step} ${x.done ? s.done : ""}`}>
            <span className={`${s.mark} ${x.blocking ? s.stop : x.done ? s.ok : s.todo}`}>
              {x.blocking ? "!" : x.done ? "✓" : "·"}
            </span>
            <span>
              <b>{x.title}</b>
              <div className={s.is}>{x.is}</div>
              <div className={s.next}>{x.next}</div>
              {!x.done &&
                (() => {
                  const a = STEP_ACTION[x.key];
                  if (!a) return null;
                  if ("says" in a)
                    return (
                      <div className={s.next} style={{ marginTop: 6 }}>
                        <b>{t("How: ")}</b>
                        {t(a.says)}
                      </div>
                    );
                  return (
                    <div style={{ marginTop: 6 }}>
                      <Button size="sm" onClick={() => (a.tab ? go(a.tab) : a.route && navigate(a.route))}>
                        {t(a.label)}
                      </Button>
                    </div>
                  );
                })()}
            </span>
          </div>
        ))}
      </div>
      <p className={s.lead} style={{ marginTop: 12 }}>
        {st.note}
      </p>
    </Card>
  );
}

/* ── people ──────────────────────────────────────────────────────────── */

function PeopleTab({
  people,
  roles,
  auth,
  onChanged,
}: {
  people: ReturnType<typeof useQuery<Person[]>>;
  roles: ReturnType<typeof useQuery<RoleRow[]>>;
  auth: ReturnType<typeof useQuery<import("../../domain/administration").AuthStatus>>;
  onChanged: () => void;
}) {
  const session = useSession();
  const source = session.source;
  const [said, setSaid] = useState<string | null>(null);
  const [invite, setInvite] = useState({ displayName: "", email: "", role: "" });
  const [invited, setInvited] = useState<{ ok: boolean; text: string } | null>(null);
  const canInvite = (auth.data?.providers ?? []).some((p) => p.enabled && p.canInvite);
  const send = useMutation({
    mutationFn: () =>
      source.adminInvite({
        displayName: invite.displayName.trim(),
        email: invite.email.trim(),
        role: invite.role,
      }),
    onSuccess: (r) => {
      const level = t(LEVEL_LABEL[r.accessLevel]).toLowerCase();
      setInvited({
        ok: true,
        text: r.alreadyListed
          ? t("{name} was already listed; {role} added, at {level}.", { name: r.displayName, role: r.role, level })
          : !r.emailSent
            ? t("{name} added as {role}, at {level}. No email was sent.", { name: r.displayName, role: r.role, level })
            : r.accountCreated
              ? t("{name} invited as {role}, at {level}. The set-password email has gone to {email}.", {
                  name: r.displayName,
                  role: r.role,
                  level,
                  email: r.email,
                })
              : t(
                  "{email} already had a sign-in account, so it was bound to {name} as {role}, at {level}. A set-password email has gone to them too.",
                  { name: r.displayName, role: r.role, level, email: r.email },
                ),
      });
      setInvite({ displayName: "", email: "", role: "" });
      onChanged();
    },
    onError: (e) => setInvited({ ok: false, text: e instanceof Error ? e.message : String(e) }),
  });
  const [form, setForm] = useState({ displayName: "", externalId: "", email: "", role: "" });

  const add = useMutation({
    mutationFn: () =>
      source.adminAddPerson({
        displayName: form.displayName.trim(),
        externalId: form.externalId.trim(),
        email: form.email.trim() || undefined,
        roles: form.role ? [form.role] : [],
      }),
    onSuccess: (r) => {
      const level = t(LEVEL_LABEL[r.accessLevel]).toLowerCase();
      setSaid(
        (r.roles.length
          ? t("{name} added at {level} with {roles}. {note}", {
              name: r.displayName,
              level,
              roles: r.roles.join(", "),
              note: r.note,
            })
          : t("{name} added at {level} with no role yet. {note}", { name: r.displayName, level, note: r.note })) +
          (r.refused.length
            ? t(" Refused: {refused}.", { refused: r.refused.map((x) => `${x.role} (${x.why})`).join("; ") })
            : ""),
      );
      setForm({ displayName: "", externalId: "", email: "", role: "" });
      onChanged();
    },
    onError: (e) => setSaid(e instanceof Error ? e.message : String(e)),
  });
  const assign = useMutation({
    mutationFn: (v: { principal: string; role: string }) => source.adminAssignRole(v.principal, v.role),
    onSuccess: onChanged,
  });
  const unassign = useMutation({
    mutationFn: (v: { principal: string; role: string }) => source.adminUnassignRole(v.principal, v.role),
    onSuccess: onChanged,
  });
  const disable = useMutation({
    mutationFn: (v: { id: string; disabled: boolean }) => source.adminSetDisabled(v.id, v.disabled),
    onSuccess: onChanged,
  });

  if (people.isPending || roles.isPending)
    return (
      <Card title="People">
        <Skeleton rows={8} />
      </Card>
    );
  const list = people.data ?? [];
  const roleRows = roles.data ?? [];
  const warnings = peopleWarnings(list, roleRows);

  return (
    <div style={{ display: "grid", gap: "var(--gap)" }}>
      {canInvite && (
        <Card title="Invite by email" sub="Creates their sign-in and emails a set-password link">
          <div className={s.form}>
            <label className={s.field}>
              {t("name")}
              <Input
                value={invite.displayName}
                onChange={(e) => setInvite({ ...invite, displayName: e.target.value })}
                placeholder="Huda Saleh"
              />
            </label>
            <label className={s.field}>
              {t("email")}
              <Input
                type="email"
                value={invite.email}
                onChange={(e) => setInvite({ ...invite, email: e.target.value })}
                placeholder="sara@tibah.example"
              />
            </label>
            <label className={s.field}>
              {t("role")}
              <Select value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })}>
                <option value="">{t("choose a role")}</option>
                {roleRows.map((r) => (
                  <option key={r.key} value={r.key}>
                    {r.name} — {t(LEVEL_LABEL[r.accessLevel]).toLowerCase()}
                  </option>
                ))}
              </Select>
            </label>
            <Button
              variant="primary"
              disabled={!invite.displayName.trim() || !invite.email.includes("@") || !invite.role || send.isPending}
              onClick={() => send.mutate()}
            >
              {send.isPending ? t("inviting…") : t("Invite")}
            </Button>
          </div>
          {invited && (
            <p className={invited.ok ? s.saved : s.stopBox} style={{ marginTop: 8 }}>
              {invited.text}
            </p>
          )}
        </Card>
      )}

      <Card
        title="Add somebody"
        sub="No email is sent; they sign in through your identity provider"
      >
        <div className={s.form}>
          <label className={s.field}>
            {t("name")}
            <Input
              value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              placeholder="Sara Al-Amri"
            />
          </label>
          <label className={s.field}>
            {t("sign-in subject")}
            <Input
              value={form.externalId}
              onChange={(e) => setForm({ ...form, externalId: e.target.value })}
              placeholder="sara.alamri"
            />
          </label>
          <label className={s.field}>
            {t("email")}
            <Input
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder={t("optional")}
            />
          </label>
          <label className={s.field}>
            {t("first role")}
            <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <option value="">{t("no role yet")}</option>
              {roleRows.map((r) => (
                <option key={r.key} value={r.key}>
                  {r.name} — {t(LEVEL_LABEL[r.accessLevel]).toLowerCase()}
                </option>
              ))}
            </Select>
          </label>
          <Button
            variant="primary"
            disabled={!form.displayName.trim() || !form.externalId.trim() || add.isPending}
            onClick={() => add.mutate()}
          >
            {add.isPending ? t("adding…") : t("Add")}
          </Button>
        </div>
        <p className={s.lead} style={{ marginTop: 8 }}>
          {t(
            "The subject is the ID your identity provider sends at sign-in. Without it, this person can't sign in.",
          )}
        </p>
        {said && (
          <p className={s.saved} style={{ marginTop: 8 }}>
            {said}
          </p>
        )}
      </Card>

      {warnings.length > 0 && (
        <Card title="Worth knowing before you read the list">
          <div style={{ display: "grid", gap: 8 }}>
            {warnings.map((w) => (
              <p key={w} className={s.warn}>
                {w}
              </p>
            ))}
          </div>
        </Card>
      )}

      <Card
        title="Everybody in this tenant"
        sub={t("{active} active, {disabled} disabled", {
          active: list.filter((p) => !p.disabledAt).length,
          disabled: list.filter((p) => p.disabledAt).length,
        })}
      >
        <div className={s.scroll}>
          <table className={s.table}>
            <thead>
              <tr>
                <th>{t("Person")}</th>
                <th>{t("Subject")}</th>
                <th>{t("Level")}</th>
                <th>{t("Roles")}</th>
                <th>{t("Add a role")}</th>
                <th>{t("Last seen")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {list.map((p) => {
                const level = levelOfPerson(p, roleRows);
                const spare = roleRows.filter((r) => !p.roles.includes(r.key));
                return (
                  <tr key={p.id} className={p.disabledAt ? s.off : undefined}>
                    <td>
                      <b>{p.displayName}</b>
                      <div className={s.dim}>{p.email ?? p.kind}</div>
                    </td>
                    <td className={s.mono}>{p.externalId ?? <span className={s.dim}>{t("none")}</span>}</td>
                    <td>
                      <Level level={level} />
                    </td>
                    <td>
                      <div className={s.chips}>
                        {p.roles.length === 0 && <span className={s.dim}>{t("none")}</span>}
                        {p.roles.map((r) => (
                          <span key={r} className={s.chip}>
                            {r}
                            <button
                              type="button"
                              aria-label={t("Remove {role} from {name}", { role: r, name: p.displayName })}
                              onClick={() => unassign.mutate({ principal: p.externalId ?? p.id, role: r })}
                            >
                              ✕
                            </button>
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <Select
                        value=""
                        aria-label={t("Add a role to {name}", { name: p.displayName })}
                        onChange={(e) =>
                          e.target.value && assign.mutate({ principal: p.externalId ?? p.id, role: e.target.value })
                        }
                      >
                        <option value="">{t("add…")}</option>
                        {spare.map((r) => (
                          <option key={r.key} value={r.key}>
                            {r.name}
                          </option>
                        ))}
                      </Select>
                    </td>
                    <td className={s.mono}>
                      {p.lastSeenAt ? p.lastSeenAt.slice(0, 10) : <span className={s.dim}>{t("never")}</span>}
                    </td>
                    <td className={s.rowActions}>
                      <Button size="sm" onClick={() => disable.mutate({ id: p.id, disabled: !p.disabledAt })}>
                        {p.disabledAt ? t("Enable") : t("Disable")}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className={s.lead} style={{ marginTop: 10 }}>
          {t(
            "Disabling blocks sign-in but keeps the record. People aren't deleted, so their names stay on the work orders they signed.",
          )}
        </p>
      </Card>
    </div>
  );
}

/* ── roles ───────────────────────────────────────────────────────────── */

function RolesTab({
  roles,
  people,
  onChanged,
}: {
  roles: ReturnType<typeof useQuery<RoleRow[]>>;
  people: ReturnType<typeof useQuery<Person[]>>;
  onChanged: () => void;
}) {
  const session = useSession();
  const [said, setSaid] = useState<string | null>(null);
  const setLevel = useMutation({
    mutationFn: (v: { key: string; level: AccessLevel }) => session.source.adminSetRoleLevel(v.key, v.level),
    onSuccess: (_r, v) => {
      setSaid(
        t("{role} now offers {level}. This changes what its holders see, not their permissions.", {
          role: v.key,
          level: t(LEVEL_LABEL[v.level]).toLowerCase(),
        }),
      );
      onChanged();
    },
    onError: (e) => setSaid(e instanceof Error ? e.message : String(e)),
  });

  if (roles.isPending)
    return (
      <Card title="Roles">
        <Skeleton rows={6} />
      </Card>
    );
  const list = roles.data ?? [];
  const holders = (key: string) => (people.data ?? []).filter((p) => p.roles.includes(key)).length;

  return (
    <Card
      title="Roles and access levels"
      sub="Levels set what's shown; permissions set what's allowed"
    >
      <div className={s.scroll}>
        <table className={s.table}>
          <thead>
            <tr>
              <th>{t("Role")}</th>
              <th>{t("Holders")}</th>
              <th>{t("Permissions")}</th>
              <th>{t("Level offered")}</th>
              <th>{t("Set")}</th>
            </tr>
          </thead>
          <tbody>
            {list.map((r) => (
              <tr key={r.key}>
                <td>
                  <b>{r.name}</b>
                  <div className={s.dim}>
                    {r.key}
                    {r.builtin ? t(" · shipped with the platform") : t(" · this tenant's own")}
                  </div>
                  {r.description && (
                    <div className={s.muted} style={{ marginTop: 3 }}>
                      {r.description}
                    </div>
                  )}
                </td>
                <td className={s.mono}>{holders(r.key)}</td>
                <td className={s.mono}>{r.permissions.includes("*") ? t("everything") : r.permissions.length}</td>
                <td>
                  <Level level={r.accessLevel} />
                </td>
                <td>
                  {r.builtin ? (
                    <span
                      className={s.dim}
                      title={t(
                        "Built-in roles are shared by all tenants, so their level can't be changed here.",
                      )}
                    >
                      {t("shipped — not editable")}
                    </span>
                  ) : (
                    <Select
                      value={r.accessLevel}
                      aria-label={t("Level for {name}", { name: r.name })}
                      onChange={(e) => setLevel.mutate({ key: r.key, level: e.target.value as AccessLevel })}
                    >
                      {LEVELS.map((l) => (
                        <option key={l} value={l}>
                          {t(LEVEL_LABEL[l])}
                        </option>
                      ))}
                    </Select>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {said && (
        <p className={s.saved} style={{ marginTop: 10 }}>
          {said}
        </p>
      )}
      <p className={s.lead} style={{ marginTop: 10 }}>
        {t(
          "Built-in roles are shared by all tenants, so you can't change their level here. Create your own role instead. A person’s level is the highest of their roles.",
        )}
      </p>
    </Card>
  );
}

/* ── sign-in ─────────────────────────────────────────────────────────── */

function SignInTab({
  auth,
  roles,
  onChanged,
}: {
  auth: ReturnType<typeof useQuery<import("../../domain/administration").AuthStatus>>;
  roles: ReturnType<typeof useQuery<RoleRow[]>>;
  onChanged: () => void;
}) {
  const source = useSession().source;
  const [said, setSaid] = useState<{ ok: boolean; text: string } | null>(null);
  const change = useMutation({
    mutationFn: (v: { id: string; change: import("../../domain/administration").ProviderChange; done: string }) =>
      source.adminUpdateProvider(v.id, v.change).then(() => v.done),
    onSuccess: (done) => {
      setSaid({ ok: true, text: done });
      onChanged();
    },
    onError: (e) => setSaid({ ok: false, text: e instanceof Error ? e.message : String(e) }),
  });
  const refreshKeys = useMutation({
    mutationFn: (v: { id: string; name: string }) =>
      source.adminRefreshProviderKeys(v.id).then((n) => ({ n, name: v.name })),
    onSuccess: ({ n, name }) => {
      setSaid({ ok: true, text: t("{name}: {n} signing keys fetched and cached.", { name, n }) });
      onChanged();
    },
    onError: (e) => setSaid({ ok: false, text: e instanceof Error ? e.message : String(e) }),
  });
  if (auth.isPending)
    return (
      <Card title="Sign-in">
        <Skeleton rows={5} />
      </Card>
    );
  const a = auth.data;
  if (!a) return null;
  return (
    <div style={{ display: "grid", gap: "var(--gap)" }}>
      {a.developmentHeaderEnabled && (
        <Card title="The development header is enabled">
          <p className={s.stopBox}>
            <b>{t("Any caller naming a tenant is trusted.")}</b>{" "}
            {rich(
              t(
                "While this is on, no permission is checked. Unset {flag} and restart the API before using real data.",
              ),
              { flag: <span className={s.mono}>SLT_DEV_AUTH</span> },
            )}
          </p>
        </Card>
      )}
      <Card
        title="How this deployment authenticates"
        sub={t("you are signed in as {principal} by {method}", {
          principal: a.principal ?? t("unknown"),
          method: a.method ?? t("unknown"),
        })}
      >
        {a.providers.length === 0 ? (
          <p className={s.lead}>
            {t(
              "No identity provider is set up, so the only way in is an issued API key. Connect your organization’s OIDC provider so people use their work account and lose access when they leave.",
            )}
          </p>
        ) : (
          <table className={s.table}>
            <thead>
              <tr>
                <th>{t("Provider")}</th>
                <th>{t("Issuer")}</th>
                <th>{t("Enabled")}</th>
                <th>{t("Cached keys")}</th>
                <th>{t("Default role")}</th>
                <th>{t("Change")}</th>
              </tr>
            </thead>
            <tbody>
              {a.providers.map((p) => (
                <tr key={p.name}>
                  <td>
                    <b>{p.name}</b>
                  </td>
                  <td className={s.mono}>{p.issuer}</td>
                  <td>{p.enabled ? t("yes") : <span className={s.dim}>{t("no")}</span>}</td>
                  <td className={s.mono}>{p.cachedKeys}</td>
                  <td className={s.mono}>
                    {p.id ? (
                      <Select
                        value={p.defaultRole ?? ""}
                        disabled={change.isPending}
                        aria-label={t("Default role for {name}", { name: p.name })}
                        onChange={(e) =>
                          change.mutate({
                            id: p.id!,
                            change: { defaultRole: e.target.value || null },
                            done: e.target.value
                              ? t("{name}: somebody signing in with no role now gets {role}.", {
                                  name: p.name,
                                  role: e.target.value,
                                })
                              : t("{name}: somebody signing in with no role now gets nothing until given one.", {
                                  name: p.name,
                                }),
                          })
                        }
                      >
                        <option value="">{t("none")}</option>
                        {(roles.data ?? []).map((r) => (
                          <option key={r.key} value={r.key}>
                            {r.key}
                          </option>
                        ))}
                      </Select>
                    ) : (
                      (p.defaultRole ?? <span className={s.dim}>{t("none")}</span>)
                    )}
                  </td>
                  <td>
                    {p.id && (
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        <Button
                          size="sm"
                          disabled={change.isPending}
                          onClick={() =>
                            change.mutate({
                              id: p.id!,
                              change: { enabled: !p.enabled },
                              done: p.enabled
                                ? t("{name} switched off: its tokens are no longer accepted.", { name: p.name })
                                : t("{name} switched on.", { name: p.name }),
                            })
                          }
                        >
                          {p.enabled ? t("Switch off") : t("Switch on")}
                        </Button>
                        <Button
                          size="sm"
                          disabled={refreshKeys.isPending}
                          onClick={() => refreshKeys.mutate({ id: p.id!, name: p.name })}
                        >
                          {t("Refresh keys")}
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {said && (
          <p className={said.ok ? s.saved : s.stopBox} style={{ marginTop: 8 }}>
            {said.text}
          </p>
        )}
        <p className={s.lead} style={{ marginTop: 8 }}>
          {t(
            "The default role goes to anyone who signs in with no role. Leave it empty and they see nothing until an administrator gives them one. You can't switch off the only sign-in provider, since that would lock everyone out.",
          )}
        </p>
      </Card>
      <Card title="About the sign-in gate on this copy">
        <p className={s.lead}>
          {t(
            "The gate here (email, password and a six-digit code) only stops people using this screen. The data is protected by the platform’s sign-in above. A device whose saved accounts are cleared must enroll again.",
          )}
        </p>
      </Card>
    </div>
  );
}
